import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Download, Users, Building2, RefreshCw, Lock, DollarSign, Image, FileSpreadsheet, Award } from "lucide-react";
import axios from "axios";
import GalleryManager from "./Admin/GalleryManager";
import SponsorLogoManager from "./Admin/SponsorLogoManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("exports"); // "exports", "gallery", or "sponsors"
  const [stats, setStats] = useState({
    registrations: 0,
    sponsors: 0,
    totalRaised: 0,
    goalAmount: 10000
  });
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setAuthenticated(true);
        toast.success("Access granted");
        fetchStats();
      }
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [regsRes, sponsorsRes, fundraisingRes] = await Promise.all([
        axios.get(`${API}/registrations`),
        axios.get(`${API}/sponsor-inquiries`),
        axios.get(`${API}/fundraising`)
      ]);
      setStats({
        registrations: regsRes.data.length,
        sponsors: sponsorsRes.data.length,
        totalRaised: fundraisingRes.data.current_amount,
        goalAmount: fundraisingRes.data.goal_amount
      });
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportRegistrations = () => {
    window.open(`${API}/registrations/export`, "_blank");
    toast.success("Downloading registrations...");
  };

  const handleExportSponsors = () => {
    window.open(`${API}/sponsor-inquiries/export`, "_blank");
    toast.success("Downloading sponsor inquiries...");
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center px-6">
        <div className="max-w-sm w-full">
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#3A5F40]/10 flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-[#3A5F40]" />
              </div>
              <h1 className="text-2xl text-[#1C1C1C]">Admin Access</h1>
              <p className="text-sm text-[#666055]">Enter password to continue</p>
            </div>
            <form onSubmit={handleLogin}>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mb-4"
                data-testid="admin-password"
              />
              <Button
                type="submit"
                className="w-full bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full"
                data-testid="admin-login-btn"
                disabled={loginLoading}
              >
                {loginLoading ? "Verifying..." : "Access Dashboard"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F4] py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl text-[#1C1C1C]" data-testid="admin-title">
              Admin Dashboard
            </h1>
            <p className="text-[#666055]">Manage registrations and sponsor inquiries</p>
          </div>
          <Button
            onClick={fetchStats}
            variant="outline"
            className="rounded-full"
            disabled={loading}
            data-testid="refresh-stats-btn"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            <span className="ml-2 hidden sm:inline">Refresh</span>
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#3A5F40]/10 flex items-center justify-center">
                <Users size={24} className="text-[#3A5F40]" />
              </div>
              <div>
                <p className="text-3xl font-medium text-[#1C1C1C]" data-testid="stat-registrations">
                  {stats.registrations}
                </p>
                <p className="text-sm text-[#666055]">Registrations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <Building2 size={24} className="text-[#C5A059]" />
              </div>
              <div>
                <p className="text-3xl font-medium text-[#1C1C1C]" data-testid="stat-sponsors">
                  {stats.sponsors}
                </p>
                <p className="text-sm text-[#666055]">Sponsor Inquiries</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#3A5F40]/10 flex items-center justify-center">
                <DollarSign size={24} className="text-[#3A5F40]" />
              </div>
              <div>
                <p className="text-3xl font-medium text-[#3A5F40]" data-testid="stat-raised">
                  ${stats.totalRaised.toLocaleString()}
                </p>
                <p className="text-sm text-[#666055]">of ${stats.goalAmount.toLocaleString()} goal</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-[#E6E2D6] pb-4 flex-wrap">
          <Button
            onClick={() => setActiveTab("exports")}
            variant={activeTab === "exports" ? "default" : "ghost"}
            className={`rounded-full ${activeTab === "exports" ? "bg-[#3A5F40] text-white" : "text-[#666055] hover:text-[#1C1C1C]"}`}
            data-testid="tab-exports"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            Data Exports
          </Button>
          <Button
            onClick={() => setActiveTab("gallery")}
            variant={activeTab === "gallery" ? "default" : "ghost"}
            className={`rounded-full ${activeTab === "gallery" ? "bg-[#3A5F40] text-white" : "text-[#666055] hover:text-[#1C1C1C]"}`}
            data-testid="tab-gallery"
          >
            <Image size={18} className="mr-2" />
            Photo Gallery
          </Button>
          <Button
            onClick={() => setActiveTab("sponsors")}
            variant={activeTab === "sponsors" ? "default" : "ghost"}
            className={`rounded-full ${activeTab === "sponsors" ? "bg-[#C5A059] text-white" : "text-[#666055] hover:text-[#1C1C1C]"}`}
            data-testid="tab-sponsors"
          >
            <Award size={18} className="mr-2" />
            Sponsor Logos
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === "exports" ? (
          <>
            {/* Export Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Registrations Export */}
              <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Users size={24} className="text-[#3A5F40]" />
                  <h2 className="text-xl text-[#1C1C1C]">Registrations</h2>
                </div>
                <p className="text-[#666055] mb-6">
                  Export all golfer registrations including contact info, player type, 
                  add-ons selected, and payment status.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handleExportRegistrations}
                    className="w-full bg-[#3A5F40] hover:bg-[#2A4430] text-white rounded-full"
                    data-testid="export-registrations-btn"
                  >
                    <Download size={18} className="mr-2" />
                    Export to Excel (CSV)
                  </Button>
                  <p className="text-xs text-center text-[#666055]">
                    Downloads as CSV file - opens in Excel, Google Sheets, Numbers
                  </p>
                </div>
              </div>

              {/* Sponsors Export */}
              <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 size={24} className="text-[#C5A059]" />
                  <h2 className="text-xl text-[#1C1C1C]">Sponsor Inquiries</h2>
                </div>
                <p className="text-[#666055] mb-6">
                  Export all sponsor inquiries including company details, contact info,
                  tier interest, and any messages.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handleExportSponsors}
                    className="w-full bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-full"
                    data-testid="export-sponsors-btn"
                  >
                    <Download size={18} className="mr-2" />
                    Export to Excel (CSV)
                  </Button>
                  <p className="text-xs text-center text-[#666055]">
                    Downloads as CSV file - opens in Excel, Google Sheets, Numbers
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-8 bg-[#3A5F40]/5 rounded-2xl p-6 md:p-8">
              <h3 className="text-lg font-medium text-[#1C1C1C] mb-4">How to Use Exported Files</h3>
              <ol className="space-y-2 text-[#666055]">
                <li className="flex gap-2">
                  <span className="font-medium text-[#3A5F40]">1.</span>
                  Click the export button to download the CSV file
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-[#3A5F40]">2.</span>
                  Open with Microsoft Excel, Google Sheets, or Apple Numbers
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-[#3A5F40]">3.</span>
                  Data includes all fields: contact info, selections, amounts, timestamps
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-[#3A5F40]">4.</span>
                  Use filters and sorting to organize data as needed
                </li>
              </ol>
            </div>
          </>
        ) : activeTab === "gallery" ? (
          <GalleryManager />
        ) : (
          <SponsorLogoManager />
        )}
      </div>
    </div>
  );
}
